import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, TableContainer, Paper, Table, TableHead,
  TableBody, TableRow, TableCell, TextField, FormControl, Select, MenuItem,
  IconButton, Menu, Tooltip, TablePagination, Button, CircularProgress, Modal,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { useLazyQuery, useMutation, gql } from '@apollo/client';

const GET_KYC_REQUESTS = gql`
query GetKycProcessingData($offset: Int, $limit: Int, $searchTerm: String, $searchType: String, $status: String) {
  getKycProcessingData(offset: $offset, limit: $limit, searchTerm: $searchTerm, searchType: $searchType, status: $status) {
    success
    data {
      kyc_id
      vin
      imei
      engine_number
      year
      vehicle_make
      vehicle_model
      fuel_type
      customer_name
      customer_mobile
      retail_invoice_number
      customer_address
      customer_email
      dealer_code
      dealer_name
      dealer_address
      dealer_town
      dealer_contact_name
      kyc_count
      kyc_approved_date
      kyc_rejected_date
      last_kyc_recieved_date
      kyc_mode
      proof_of_address
      proof_of_identity
      ticket_created
      status
      rejection_reason
      request_id
      project_name
      device_model
      device_serial_number
      device_imei_number
      iccid_number
      customer_number
      customer_otp_number
      registration_state
      rto_name
      rto_number
      dealer_contact_number
      alternate_dealer_name
      alternate_dealer_number
      sim_activation_status
      msisdn_1
      msisdn_2
      vehicle_type
      year_of_manufacture
      comman_layer_cert
      common_layer_cert_issued_date
      fitment_cert
      fitment_cert_issued_date
    }
    count
    error
  }
}
 `;

const UPDATE_KYC_STATUS = gql`
  mutation Mutation($input: KYCStatusUpdateInput!) {
    updateKycStatus(input: $input) {
      success
      message
      error
    }
  }
`;


const KYC_FILTER_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'KYC_APPROVED', value: 'KYC_APPROVED' },
  { label: 'KYC_REJECTED', value: 'KYC_REJECTED' },
  { label: 'KYC_RECEIVED', value: 'KYC_RECEIVED' },
];

interface KycRequest {
  kyc_id: string;
  vin: string;
  imei: string;
  engine_number: string;
  year: string;
  vehicle_make: string;
  vehicle_model: string;
  fuel_type: string;
  customer_name: string;
  customer_mobile: string;
  retail_invoice_number: string;
  customer_address: string;
  customer_email: string;
  dealer_code: string;
  dealer_name: string;
  dealer_address: string;
  dealer_town: string;
  dealer_contact_name: string;
  kyc_count: number;
  kyc_approved_date: string;
  kyc_rejected_date: string;
  last_kyc_recieved_date: string;
  kyc_mode: string;
  proof_of_address: string;
  proof_of_identity: string;
  ticket_created: string;
  status: string;
  rejection_reason: string;
}

const formatEpochToIST = (epochString: string | null): string => {
  if (!epochString) {
    return 'N/A';
  }
  const epoch = parseInt(epochString, 10);
  if (isNaN(epoch)) {
    return 'N/A';
  }
  const date = new Date(epoch * 1000);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  };
  return date.toLocaleString('en-GB', options);
};

const KycDetails = () => {
  const [searchType, setSearchType] = useState('vin');
  const [inputValue, setInputValue] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [kycFilter, setKycFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [openModal, setOpenModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [processingVin, setProcessingVin] = useState<string | null>(null);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [selectedItem, setSelectedItem] = useState<KycRequest | null>(null);
  const [rejectionRemark, setRejectionRemark] = useState('');
  const [fetchData, { loading, error, data }] = useLazyQuery(GET_KYC_REQUESTS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchWithDebounce = useCallback(
    (overrides?: { inputValue?: string; searchType?: string; kycFilter?: string; rowsPerPage?: number }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const term = overrides?.inputValue ?? inputValue;
        const type = overrides?.searchType ?? searchType;
        const filter = overrides?.kycFilter ?? kycFilter;
        const rows = overrides?.rowsPerPage ?? rowsPerPage;
        fetchData({
          variables: {
            offset: 0,
            limit: rows,
            searchTerm: term,
            searchType: type,
            status: filter === 'ALL' ? null : filter,
          },
        });
      }, 500);
    },
    [inputValue, searchType, kycFilter, rowsPerPage, fetchData]
  );

  useEffect(() => {
    fetchData({
      variables: { offset: 0, limit: rowsPerPage, searchTerm: '', searchType: 'vin', status: null },
    });
  }, []);
  const totalCount = data?.getKycProcessingData?.count ?? 0;
  const [updateKycStatus] = useMutation(UPDATE_KYC_STATUS);

  const tableColumns = [
    'Sr.No', 'VIN', 'IMEI', 'Engine Number', 'Year', 'Vehicle Make',
    'Vehicle Model', 'Fuel Type', 'Customer Name', 'Customer Mobile',
    'Retail Invoice Number', 'Customer Address', 'Customer Email', 'Dealer Code',
    'Dealer Name', 'Dealer Address', 'Dealer Town', 'Dealer Contact Name',
    'KYC Count', 'KYC Approved Date', 'KYC Rejected Date', 'Last KYC Received Date',
    'KYC Mode', 'Proof of Address', 'Proof of Identity', 'Ticket Created', 'Status',
    'Rejection Reason', 'Actions'
  ];

  const cellStyle = {
    fontSize: { xs: '0.8rem', sm: '0.9rem' },
    padding: { xs: '4px', sm: '8px' },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderBottom: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': { borderRight: 'none' },
  };

  const handleUpdateStatus = async (status: 'APPROVE' | 'REJECT') => {
    if (!selectedItem) return;

    setProcessingVin(selectedItem.vin);
    setOpenConfirmDialog(false);

    try {
      const kycResult = status === 'APPROVE' ? 'KYC_APPROVED' : 'KYC_REJECTED';

      const input = {
        kyc_id: selectedItem.kyc_id,
        kyc_result: kycResult,
        ...(status === 'REJECT' && { reason: rejectionRemark })
      };

      const { data } = await updateKycStatus({ variables: { input } });

      if (data?.updateKycStatus?.success) {
        console.log(`KYC ${status === 'APPROVE' ? 'approved' : 'rejected'} successfully:`, data.updateKycStatus.message);
        fetchData({ variables: { offset: page * rowsPerPage, limit: rowsPerPage, searchTerm: inputValue, searchType, status: kycFilter === 'ALL' ? null : kycFilter } });
      } else {
        console.error(`Error ${status === 'APPROVE' ? 'approving' : 'rejecting'} KYC:`, data?.updateKycStatus?.message || data?.updateKycStatus?.error);
      }
    } catch (err) {
      console.error('Network or server error:', err);
    } finally {
      setProcessingVin(null);
      setSelectedItem(null);
      setRejectionRemark('');
    }
  };

  const openApprovalDialog = (item: KycRequest) => {
    setSelectedItem(item);
    setDialogAction('APPROVE');
    setOpenConfirmDialog(true);
  };

  const openRejectionDialog = (item: KycRequest) => {
    setSelectedItem(item);
    setDialogAction('REJECT');
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setSelectedItem(null);
    setDialogAction(null);
    setRejectionRemark('');
  };

  interface ProofPreviewParams {
    documentType: string;
    documentUrl: string;
  }

  const handleProofPreview = ({ documentType, documentUrl }: ProofPreviewParams) => {
    setModalTitle(documentType);
    setModalImageSrc(documentUrl);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalImageSrc('');
    setModalTitle('');
  };

  const filteredData: KycRequest[] = data?.getKycProcessingData?.data || [];

  if (error) return <Typography color="error" sx={{ textAlign: 'center', mt: 4 }}>Error fetching data: {error.message}</Typography>;

  const getButtonStyles = (status: string, isProcessing: boolean, isApproveButton: boolean) => {
    const isFinalStatus = status === 'KYC_APPROVED' || status === 'KYC_REJECTED';
    const isButtonDisabled = isFinalStatus || isProcessing;

    const baseStyles = {
      borderRadius: '12px',
      height: '24px',
      padding: '0 12px',
      fontSize: '12px',
      fontWeight: 'normal',
      textTransform: 'none',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      minWidth: '75px',
    };

    if (isButtonDisabled) {
      return {
        ...baseStyles,
        backgroundColor: '#e0e0e0',
        color: '#a0a0a0',
        border: '1px solid #d0d0d0',
        cursor: 'not-allowed',
        opacity: 0.5,
      };
    } else {
      return {
        ...baseStyles,
        backgroundColor: isApproveButton ? '#58B80F1A' : '#FF00001A',
        color: isApproveButton ? '#58B80F' : '#FF0000',
        border: isApproveButton ? '1px solid #58B80F' : '1px solid #FF0000',
        cursor: 'pointer',
        opacity: 1,
      };
    }
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        KYC Details
      </Typography>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
            <Box display="flex" gap={1} alignItems="center" sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <FormControl size="small" sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}>
                <Select value={searchType} onChange={e => { setSearchType(e.target.value); fetchWithDebounce({ searchType: e.target.value }); }} displayEmpty>
                  <MenuItem value="vin">Search by VIN</MenuItem>
                  <MenuItem value="imei">Search by IMEI</MenuItem>
                  <MenuItem value="engine_number">Search by Engine No</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                variant="outlined"
                placeholder={
                  searchType === 'vin' ? 'Search by VIN' :
                    searchType === 'imei' ? 'Search by IMEI' :
                      searchType === 'engine_number' ? 'Search by Engine No' : 'Search...'
                }
                value={inputValue}
                onChange={e => { setInputValue(e.target.value); fetchWithDebounce({ inputValue: e.target.value }); }}
                sx={{ background: '#f5f5f5', minWidth: 120, flex: 1 }}
                inputProps={{ maxLength: 32 }}
              />
              <IconButton sx={{ ml: 1, color: '#000000ff', flex: '0 0 auto' }} onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                <FilterListIcon />
              </IconButton>
              <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                {KYC_FILTER_OPTIONS.map(option => (
                  <MenuItem key={option.value} selected={kycFilter === option.value} onClick={() => { setKycFilter(option.value); setFilterAnchorEl(null); fetchWithDebounce({ kycFilter: option.value }); }}>
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, py: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2, width: '100%', minWidth: 1200, background: '#fff', overflow: 'visible' }}>
              <Table stickyHeader size="small" sx={{ minWidth: 1200, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header, index) => (
                      <TableCell
                        key={header}
                        align={index === 0 ? 'left' : 'center'}
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                          minWidth: (header === 'Customer Address' || header === 'Rejection Reason') ? 200 : 'auto',
                          maxWidth: (header === 'Customer Address' || header === 'Rejection Reason') ? 300 : 'auto',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        <CircularProgress size={40} />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No KYC records
                      </TableCell>
                    </TableRow>
                  ) : (
                    (filteredData as KycRequest[]).map((row, idx: number) => (
                      <TableRow key={idx}
                      hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                        <TableCell align="center" sx={cellStyle}>{idx + 1 + page * rowsPerPage}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={row.vin || "N/A"} placement="top" arrow>
                            <span>{row.vin || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}> <Tooltip title ={row.imei || "N/A"} placement='top' arrow>
                        <span>{row.imei || "N/A"}</span></Tooltip></TableCell>
                        <TableCell align="center" sx={cellStyle}><Tooltip title ={row.engine_number || "N/A"} placement = "top" arrow >
                        <span>{row.engine_number || "N/A"}</span></Tooltip></TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.year || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.vehicle_make || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.vehicle_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.fuel_type || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.customer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.customer_mobile || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.retail_invoice_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={row.customer_address || 'N/A'} placement='top' arrow>
                            <span>{row.customer_address || 'N/A'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.customer_email || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.dealer_code || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.dealer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={row.dealer_address || 'N/A'}>
                            <span>{row.dealer_address || 'N/A'}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.dealer_town || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.dealer_contact_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.kyc_count || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpochToIST(row.kyc_approved_date)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpochToIST(row.kyc_rejected_date)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatEpochToIST(row.last_kyc_recieved_date)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.kyc_mode || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            disabled={!row.proof_of_address}
                            onClick={() => row.proof_of_address && handleProofPreview({ documentType: 'Proof of Address', documentUrl: row.proof_of_address })}
                            sx={{
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: row.proof_of_address ? '0px 2px 4px rgba(0,0,0,0.1)' : 'none',
                              minWidth: '108px',
                              backgroundColor: row.proof_of_address ? '#58B80F1A' : undefined,
                              color: row.proof_of_address ? '#58B80F' : undefined,
                              border: row.proof_of_address ? '1px solid #58B80F' : '1px solid #d0d0d0',
                              '&:hover': { backgroundColor: row.proof_of_address ? '#58B80F33' : undefined },
                              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#a0a0a0', border: '1px solid #d0d0d0', boxShadow: 'none' },
                            }}
                          >
                            PREVIEW
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            disabled={!row.proof_of_identity}
                            onClick={() => row.proof_of_identity && handleProofPreview({ documentType: 'Proof of Identity', documentUrl: row.proof_of_identity })}
                            sx={{
                              borderRadius: '12px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: row.proof_of_identity ? '0px 2px 4px rgba(0,0,0,0.1)' : 'none',
                              minWidth: '108px',
                              backgroundColor: row.proof_of_identity ? '#58B80F1A' : undefined,
                              color: row.proof_of_identity ? '#58B80F' : undefined,
                              border: row.proof_of_identity ? '1px solid #58B80F' : '1px solid #d0d0d0',
                              '&:hover': { backgroundColor: row.proof_of_identity ? '#58B80F33' : undefined },
                              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#a0a0a0', border: '1px solid #d0d0d0', boxShadow: 'none' },
                            }}
                          >
                            PREVIEW
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.ticket_created || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip title={row.rejection_reason || 'N/A'} placement='top' arrow>
                            <span>{row.rejection_reason || "N/A"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" sx={{ ...cellStyle, display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            onClick={() => openApprovalDialog(row)}
                            disabled={['KYC_APPROVED', 'KYC_REJECTED'].includes(row.status) || processingVin === row.vin}
                            sx={{
                              ...getButtonStyles(row.status, processingVin === row.vin, true)
                            }}
                          >
                            APPROVE
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => openRejectionDialog(row)}
                            disabled={['KYC_APPROVED', 'KYC_REJECTED'].includes(row.status) || processingVin === row.vin}
                            sx={{
                              ...getButtonStyles(row.status, processingVin === row.vin, false)
                            }}
                          >
                            REJECT
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box sx={{ position: 'sticky', bottom: 0, zIndex: 20, background: '#fff', borderTop: '1px solid #e0e0e0', pt: 2, pb: 2, mt: 'auto', overflowX: 'auto' }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => { setPage(newPage); fetchData({ variables: { offset: newPage * rowsPerPage, limit: rowsPerPage, searchTerm: inputValue, searchType, status: kycFilter === 'ALL' ? null : kycFilter } }); }}
            onRowsPerPageChange={event => { const rpp = parseInt(event.target.value, 10); setRowsPerPage(rpp); setPage(0); fetchWithDebounce({ rowsPerPage: rpp }); }}
            labelRowsPerPage="Rows per page:"
            sx={{
              '.MuiTablePagination-toolbar': {
                px: 0,
                flexWrap: 'wrap',
                justifyContent: 'center',
                '@media (min-width: 600px)': {
                  flexWrap: 'nowrap',
                  justifyContent: 'space-between'
                }
              }
            }}
          />
        </Box>
      </Card>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          p: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: '100vw',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '16px',
            position: 'relative',
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {modalTitle}
          </Typography>
          <Box sx={{ maxWidth: '100%', maxHeight: '100%', overflow: 'auto' }}>
            <img src={modalImageSrc} alt={modalTitle} style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        {dialogAction === 'APPROVE' ? (
          <>
            <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 5 }}>
              <IconButton
            aria-label="close"
            onClick={handleCloseConfirmDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color:"black",
            }}
          >
            <CloseIcon />
          </IconButton>
              <Typography variant="h6" fontWeight="bold">
                Are you sure you want to approve this KYC Details?
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ p: '24px' }}>

            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0, gap: 2 }}>
              <Button
                onClick={handleCloseConfirmDialog}
                sx={{
                  borderRadius: '10px',
                  border: '1px solid #e0e0e0',
                  color: '#000',
                  padding: '8px 24px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': { border: '1px solid #d0d0d0', bgcolor: '#f5f5f5' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleUpdateStatus('APPROVE')}
                variant="contained"
                sx={{
                  borderRadius: '10px',
                  bgcolor: '#FF0000',
                  color: '#fff',
                  padding: '8px 24px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#cc0000',
                  },
                }}
              >
                Approve
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                Enter the remarks
              </Typography>
              <IconButton
            aria-label="close"
            onClick={handleCloseConfirmDialog}
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              color:"black",
            }}
          >
            <CloseIcon />
          </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: '50px' }}>
              <TextField
                autoFocus
                margin="dense"
                id="remarks"
                placeholder="Remarks"
                type="text"
                fullWidth
                variant="outlined"
                value={rejectionRemark}
                multiline
                rows={3}
                onChange={(e) => setRejectionRemark(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    mt: 2,
                    borderRadius: '5px',
                    padding: '12px 25px',
                  inputProps: {
                    maxLength: 100,
                  } 
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0, gap: 2 }}>
              <Button
                onClick={handleCloseConfirmDialog}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  color: '#000',
                  padding: '8px 24px',
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleUpdateStatus('REJECT')}
                variant="contained"
                disabled={!rejectionRemark}
                sx={{
                  borderRadius: '12px',
                  bgcolor: '#FF0000',
                  color: '#fff',
                  padding: '8px 24px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#cc0000',
                  },
                }}
              >
                Submit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default KycDetails;